import { OdinIcon, OdinIconSize } from '@myosh/odin-components';
import type { DropDownResult } from '@myosh/odin-components';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PersonFieldSubFieldsProps } from '../../../@types/forms';
import { useGetUserByIdQuery } from '../../../redux/services/user';
import { UserFields } from '../../../utils/user-config';

interface PersonSubFieldComponentProps extends PersonFieldSubFieldsProps {
  person?: DropDownResult;
  readOnly?: boolean;
}

const PersonSubFieldValues = [UserFields.occupation, UserFields.secondaryOccupations, UserFields.manager].join(',');

const PersonSubFieldComponent = ({
  person,
  showPersonsOccupations,
  showPersonsManager,
  readOnly,
}: PersonSubFieldComponentProps) => {
  const { t } = useTranslation();

  const { data, isFetching } = useGetUserByIdQuery(
    { id: person?.value as number, fields: PersonSubFieldValues },
    {
      skip: !person || !person.value,
    }
  );

  const hasOccupation =
    showPersonsOccupations && person && person.value !== null && !isFetching && Boolean(data?.occupation);

  const secondaryOccupations =
    !isFetching && data?.secondaryOccupations && Array.isArray(data.secondaryOccupations)
      ? (data.secondaryOccupations as DropDownResult[])
      : [];

  const hasSecondaryOccupations =
    showPersonsOccupations && person && person.value !== null && secondaryOccupations.length > 0;

  const hasManager = showPersonsManager && person && person.value !== null && !isFetching && Boolean(data?.manager);

  if (!hasOccupation && !hasSecondaryOccupations && !hasManager) {
    return null;
  }

  const chipStyle = clsx(
    'text-mono-1 mr-1 mb-1 pl-2 py-0.5 rounded-lg whitespace-nowrap flex items-center justify-center',
    { 'bg-primary-2 pr-1': !readOnly },
    { 'bg-gray-3 pr-2': readOnly }
  );

  return (
    <div className="mt-2">
      <div className="rounded border border-solid border-gray-4 bg-gray-5 px-2 pb-2 shadow-sm">
        {hasOccupation && (
          <div className="mt-2">
            <div className="text-sm font-medium">
              <div className="mr-1 flex flex-row items-center gap-1">
                <OdinIcon icon="Briefcase" size={OdinIconSize.ExtraSmall} />
                {t('occupation')}
              </div>
              <div className="font-normal">{(data?.occupation as DropDownResult).text}</div>
            </div>
          </div>
        )}
        {hasManager && (
          <div className="mt-2">
            <div className="text-sm font-medium">
              <div className="mr-1 flex flex-row items-center gap-1">
                <OdinIcon icon="User" size={OdinIconSize.ExtraSmall} />
                {t('manager')}
              </div>
              <div className="font-normal">{(data?.manager as DropDownResult).text}</div>
            </div>
          </div>
        )}
        {hasSecondaryOccupations && (
          <div className="mt-2">
            <div className="text-sm font-medium">
              <div className="mr-1 flex flex-row items-center gap-1">
                <OdinIcon icon="Tag" size={OdinIconSize.ExtraSmall} />
                {t('secondary-occupation')}
              </div>
              <div className="flex flex-1 flex-wrap pt-1 text-xs">
                {secondaryOccupations.map((secondaryOccupation) => (
                  <div className={chipStyle} key={secondaryOccupation.value as number}>
                    {secondaryOccupation.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(PersonSubFieldComponent);
